import React, { createContext, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useCall } from "contexts/Call"
import { useAlertMessage } from "hooks/useAlertMessage"
import { isEmpty, isEqual } from "lodash"
import { createContextHook } from "utils/contextHelpers"
import { dataService } from "utils/dataService"
import { getLoggerInstance } from "utils/logger"
import { toRedacted } from "martti-agent-services"
import { NON_BILLABLE_OPTIONS } from "./constants"

import { usePartners } from "./hooks/usePartners"
import { useAccessCode } from "./hooks/useAccessCode"
import { useCustomFields } from "./hooks/useCustomFields"
import { useCallFormFields } from "./hooks/useCallFormFields"
import { useDepartmentsForForm } from "./hooks/useDepartmentsForForm"
import { usePartnerAccessCode } from "hooks/usePartnerAccessCode"
import { useDebouncedForm } from "hooks/useDebouncedForm"
import { usePureCallback } from "hooks/usePureCallback"
import { useIntakeQuestionsForInteraction } from "hooks/useIntakeQuestionsForInteraction"
import { useCurrentEncounter } from "./hooks/useCurrentEncounter"
import { callFormDataManager } from "./CallFormDataManager"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { MULTI_PATIENT_ENABLED } from "constants/featureFlags"
import { LISTEN } from "constants/callTypes"
import { useMultiPatientRefreshInterval } from "./hooks/useMultiPatientRefreshInterval"
import {
  createAnswersFromFieldNames,
  mergeIntakeAnswers,
  formatAnswers,
  formatAnswersRedacted,
  parseAnswerValue,
  questionsToSave,
} from "./utils/intakeAnswers"
const { HttpErrors } = require("@cloudbreakus/network-utilities")

const logger = getLoggerInstance()

const CallFormDataContext = createContext()

export const _showSpinner = (bouncing, refreshing, saved) => bouncing || refreshing || !saved

const _asComparable = (value) => (value === undefined || value === null ? "" : value)

export const _isLocallyEdited = (current, lastLoaded, questionId) =>
  _asComparable(current[questionId]) !== _asComparable(lastLoaded[questionId])

export const _shouldTakeServerValue = (current, lastLoaded, questionId, serverValue) => {
  if (_isLocallyEdited(current, lastLoaded, questionId)) return false

  const localValue = current[questionId]
  const localIsEmpty = localValue === undefined || localValue === ""
  const serverHasValue = serverValue !== undefined && serverValue !== ""

  return localIsEmpty && serverHasValue
}

const _isPollInterval = (intervalMs) => Number.isFinite(intervalMs) && intervalMs > 0

export const _mergeWithLocalEdits = (current, loaded, lastLoaded) => {
  const next = { ...loaded }
  Object.keys(current).forEach((questionId) => {
    if (_isLocallyEdited(current, lastLoaded, questionId)) next[questionId] = current[questionId]
  })
  return next
}

const _CallFormDataProvider = ({ children }) => {
  const { answeredCall, exitConfirmation } = useCall()
  const listeningIn = answeredCall?._action === LISTEN
  const { showErrorToast, showInfoToast } = useAlertMessage()
  const debounceMs = useConfigFlag("callsheet.saveDelayMilliseconds", 1500)
  const [serverState, setServerState] = useState({})
  const [changeDetected, setChangeDetected] = useState(false)
  const [validatedAccessCode, setValidatedAccessCode] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [wizardStatus, setWizardStatus] = useState("partial") // options: "empty", "partial", "complete"
  const [intakeAnswers, setIntakeAnswers] = useState({})
  const [answersForEncounterId, setAnswersForEncounterId] = useState(undefined)
  const answersForEncounterIdRef = useRef(undefined)
  const liveEncounterIdRef = useRef(undefined)
  const lastLoadedAnswersRef = useRef({})
  const encounterMovedOnNotified = useRef(null)
  const [answersRefreshToken, setAnswersRefreshToken] = useState(0)
  const answersTokenRef = useRef(0)
  const answersWaitersRef = useRef([])
  const [answersChangedRecently, setAnswersChangedRecently] = useState(false)
  const [intakeAnswersChangeDetected, setIntakeAnswersChangeDetected] = useState(false)
  const [pendingIntakeRefetch, setPendingIntakeRefetch] = useState(false)

  const {
    refreshCallSheet,
    callSheetVersion,

    interactionId,
    interactionName,
    department,
    departmentName,
    language,
    nonBillableReasons,
    unitId,
    partnerId,
    interactionPartnerId,
    originalPartnerId,
    originalPartnerName,
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    providerFirstName,
    providerLastName,
    setPartnerId,
    setDepartment,
    setNonBillableReasons,
    setPatientFirstName,
    setPatientLastName,
    setPatientMRN,
    setPatientDOB,
    setProviderFirstName,
    setProviderLastName,

    phoneNumberAC,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
    setVisitId,
    setSentaraAccessCode,
    setNyuAccessCode: setNYUAccessCode,
    setMemberIdNumber,
    setLocation,
    setPhoneNumberAC,
    setBchOaklandHospitalConfirmation: setBCHOaklandHospitalConfirmation,
    setCallerFirstName,
    setCallerLastName,

    customerPhoneNumber,
    ivrAccessCode,
    meetingUrl,
    accessCodeValidationRequired,

    showCollectLocation,
    showCollectDepartment,
    setShowCollectDepartment,
  } = useCallFormFields(answeredCall, setServerState)

  const {
    currentEncounterId,
    currentEncounterSequenceNumber,
    hasOpenEncounter,
    isPastFirstEncounter,
    canStartNextPatient,
    startNextPatient,
    refetchEncounter,
    isEncounterFinalized,
    loading: isFetchingEncounter,
  } = useCurrentEncounter({ interactionId, listeningIn })

  const { accessCodeObject, loadingAccessCode, getPartnerAccessCode } = usePartnerAccessCode(
    partnerId || originalPartnerId || interactionPartnerId,
    validatedAccessCode
  )

  const partnerIdForTree = originalPartnerId ?? interactionPartnerId
  const { partners, partnersLoading } = usePartners({ partnerId: partnerIdForTree, showCollectLocation, answeredCall })

  const { accessCode: customFieldsData, departmentId: assignedDepartmentId } = useCustomFields(
    unitId,
    accessCodeObject,
    answeredCall
  )

  const { departmentOptions } = useDepartmentsForForm(
    setDepartment,
    setServerState,
    partnerId || interactionPartnerId,
    assignedDepartmentId,
    answeredCall
  )

  const {
    questions: intakeQuestions,
    refetch: intakeRefetch,
    error: intakeError,
    loading: intakeLoading,
  } = useIntakeQuestionsForInteraction({
    interactionId,
  })

  const accessCodeValidationData = useAccessCode({
    validatedAccessCode,
    setValidatedAccessCode,
    ivrAccessCode,
    unitId,
    answeredCall,
    interactionId,
    accessCodeObject,
    loadingAccessCode,
    getPartnerAccessCode,
    refreshCallSheet,
    accessCodeValidationRequired,
  })

  const intakeAnswerMode = useConfigFlag("callsheet.intakeAnswer")

  const multiPatientEnabled = useConfigFlag(MULTI_PATIENT_ENABLED)
  const answersRefreshIntervalSeconds = useMultiPatientRefreshInterval({ listeningIn })

  const { bouncing, onDebouncedChange } = useDebouncedForm(debounceMs, setChangeDetected)

  const { bouncing: intakeAnswersBouncing, onDebouncedChange: onDebouncedIntakeAnswerChange } = useDebouncedForm(
    debounceMs,
    setIntakeAnswersChangeDetected
  )

  const clientState = useMemo(
    () => ({
      nonBillableReason: nonBillableReasons === NON_BILLABLE_OPTIONS.NONE.key ? null : nonBillableReasons,
      departmentId: department,
      partnerId,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      phoneNumberAC,
      providerFirstName,
      providerLastName,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
    }),
    [
      nonBillableReasons,
      department,
      partnerId,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      phoneNumberAC,
      providerFirstName,
      providerLastName,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
    ]
  )

  const saved = useMemo(
    () => (isEmpty(serverState) ? true : isEqual(serverState, clientState)),
    [serverState, clientState]
  )

  const saveCallSheet = useCallback(
    (payload) => {
      return dataService.Interactions.CallSheet.set({
        interactionId,
        payload,
      })
    },
    [interactionId]
  )

  const submitCallFormData = useCallback(async () => {
    if (listeningIn) {
      callFormDataManager.onSubmitted()
      return
    }
    let payload
    try {
      const includePartnerId = partnerId && partnerId !== originalPartnerId
      payload = {
        nonBillableReason: nonBillableReasons === NON_BILLABLE_OPTIONS.NONE.key ? null : nonBillableReasons,
        departmentId: department,
        ...(includePartnerId && { partnerId }),
        patientFirstName,
        patientLastName,
        patientMRN,
        patientDOB,
        phoneNumberAC,
        providerFirstName,
        providerLastName,
        bchOaklandHospitalConfirmation,
        location,
        memberIdNumber,
        nyuAccessCode,
        sentaraAccessCode,
        visitId,
        callerFirstName,
        callerLastName,
      }
      const prevState = serverState
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Network timeout: saveCallSheet took too long")), 15000)
      )
      await Promise.race([saveCallSheet(payload), timeoutPromise])
      setServerState(payload)
      logger.info(`Call sheet updated`, toRedacted(payload))
      if (
        (payload.partnerId && payload.partnerId !== prevState.partnerId) ||
        (payload.departmentId && payload.departmentId !== prevState.departmentId)
      ) {
        intakeRefetch()
      } else {
        setPendingIntakeRefetch(false)
      }
    } catch (err) {
      if (!(err instanceof HttpErrors.ThrottledNetworkError)) {
        showErrorToast("Call sheet failed to update, please try again.")
      }
      logger.error(`Call sheet failed to update`, err, toRedacted(payload))
      setPendingIntakeRefetch(false)
    }
    callFormDataManager.onSubmitted()
  }, [
    listeningIn,
    originalPartnerId,
    nonBillableReasons,
    department,
    partnerId,
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    phoneNumberAC,
    providerFirstName,
    providerLastName,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
    saveCallSheet,
    showErrorToast,
    intakeRefetch,
    serverState,
  ])

  const onNonBillableReasons = onDebouncedChange(setNonBillableReasons)
  const debouncedSetPartnerId = onDebouncedChange(setPartnerId)
  const onPartner = useCallback(
    (id) => {
      const findInTree = (nodes, targetId) => {
        for (const node of nodes || []) {
          if (node.value === targetId) return node
          const found = findInTree(node.children, targetId)
          if (found) return found
        }
        return null
      }
      const selected = findInTree(partners, id)
      // Require an explicit true to collect so missing flags don't wipe assigned departments.
      // Unknown IDs keep the safeguard and clear as before.
      const collectsDepartment = selected ? selected.showCollectDepartment === true : true
      if (selected) {
        setShowCollectDepartment(collectsDepartment)
      }
      if (id !== partnerId) {
        // A location that doesn't collect a department keeps the one assigned by the Martti unit
        // or access code; clearing it here would save the interaction with no department. Until
        // the unit loads, keep the sheet's department, unless it was picked from a collected list.
        const keptDepartment = assignedDepartmentId || (showCollectDepartment ? "" : department)
        setDepartment(collectsDepartment ? "" : keptDepartment)
        setPendingIntakeRefetch(true)
      }
      debouncedSetPartnerId(id)
    },
    [
      partners,
      partnerId,
      assignedDepartmentId,
      showCollectDepartment,
      department,
      debouncedSetPartnerId,
      setShowCollectDepartment,
      setDepartment,
    ]
  )
  const debouncedSetDepartment = onDebouncedChange(setDepartment)
  const onDepartment = useCallback(
    (val) => {
      if (val !== department) {
        setPendingIntakeRefetch(true)
      }
      debouncedSetDepartment(val)
    },
    [department, debouncedSetDepartment]
  )
  const onPatientFirstName = onDebouncedChange(setPatientFirstName)
  const onPatientLastName = onDebouncedChange(setPatientLastName)
  const onPatientMRN = onDebouncedChange(setPatientMRN)
  const onProviderFirstName = onDebouncedChange(setProviderFirstName)
  const onProviderLastName = onDebouncedChange(setProviderLastName)

  const onVisitId = onDebouncedChange(setVisitId)
  const onSentaraAccessCode = onDebouncedChange(setSentaraAccessCode)
  const onNYUAccessCode = onDebouncedChange(setNYUAccessCode)
  const onMemberIdNumber = onDebouncedChange(setMemberIdNumber)
  const onLocation = onDebouncedChange(setLocation)
  const onPhoneNumberAccessCode = onDebouncedChange(setPhoneNumberAC)
  const onBCHOaklandHospital = onDebouncedChange(setBCHOaklandHospitalConfirmation)
  const onCallerFirstName = onDebouncedChange(setCallerFirstName)
  const onCallerLastName = onDebouncedChange(setCallerLastName)

  const onPatientDOB = useMemo(() => {
    return onDebouncedChange(setPatientDOB)
  }, [onDebouncedChange, setPatientDOB])

  const saveIntakeAnswers = useCallback(
    async (intakeQuestions) => {
      const issuedForEncounterId = currentEncounterId
      try {
        let currentIntakeAnswers = intakeAnswers
        let tableAnswers = {}

        if ((intakeAnswerMode === "new" || intakeAnswerMode === "both") && interactionId) {
          if (typeof dataService.IntakeQuestions?.getAnswers === "function") {
            let fetchTimer
            try {
              const timeoutPromise = new Promise((_, reject) => {
                fetchTimer = setTimeout(
                  () => reject(new Error("Network timeout: getIntakeAnswers took too long")),
                  5000
                )
              })
              const response = await Promise.race([
                dataService.IntakeQuestions.getAnswers({
                  interactionId,
                  encounterId: issuedForEncounterId,
                  intakeQuestions,
                }),
                timeoutPromise,
              ])
              tableAnswers = response?.data || {}
            } catch (fetchErr) {
              logger.error(`Failed to refetch intake answers before save`, fetchErr)
            } finally {
              clearTimeout(fetchTimer)
            }
          }
        }

        // The call can advance while the refetch is in flight. Encounter answers belong to the
        // encounter that has gone, but interaction answers are call-level and stay valid, so narrow
        // the save rather than dropping the interpreter's typing along with it
        const encounterMovedDuringRefetch = liveEncounterIdRef.current !== issuedForEncounterId
        if (encounterMovedDuringRefetch) {
          logger.debug(
            `Narrowing intake save to interaction answers for encounter ${issuedForEncounterId}; the call has moved to ${liveEncounterIdRef.current}`
          )
        }

        const mergedAnswers = { ...tableAnswers, ...intakeAnswers }
        const lastLoadedBeforeSave = lastLoadedAnswersRef.current
        intakeQuestions?.forEach((question) => {
          const serverVal = parseAnswerValue(tableAnswers[question.id], question.itemType)
          if (_shouldTakeServerValue(intakeAnswers, lastLoadedBeforeSave, question.id, serverVal)) {
            mergedAnswers[question.id] = serverVal
          }
        })
        currentIntakeAnswers = mergedAnswers

        const changedQuestions = intakeQuestions?.filter((question) =>
          _isLocallyEdited(intakeAnswers, lastLoadedBeforeSave, question.id)
        )
        const savableQuestions = questionsToSave(changedQuestions, hasOpenEncounter && !encounterMovedDuringRefetch)

        const answers = formatAnswers(savableQuestions, currentIntakeAnswers)
        if (!answers?.length || !interactionId) return

        let saveTimer
        try {
          const timeoutPromise = new Promise((_, reject) => {
            saveTimer = setTimeout(() => reject(new Error("Network timeout: upsertIntakeAnswers took too long")), 15000)
          })

          await Promise.race([
            dataService.IntakeQuestions.upsertAnswers({ interactionId, answers, currentEncounterId }),
            timeoutPromise,
          ])
        } finally {
          clearTimeout(saveTimer)
        }

        // The call can also advance while the save itself is in flight. Merging the response now
        // would put this patient's server values into the next patient's form
        if (liveEncounterIdRef.current !== issuedForEncounterId) {
          logger.warn(
            `Abandoning intake save completion for encounter ${issuedForEncounterId}; the call has moved to ${liveEncounterIdRef.current}`
          )
          return
        }

        setIntakeAnswers((current) => {
          const next = { ...current }
          intakeQuestions?.forEach((question) => {
            const serverValue = parseAnswerValue(tableAnswers[question.id], question.itemType)
            if (_shouldTakeServerValue(current, lastLoadedBeforeSave, question.id, serverValue)) {
              next[question.id] = serverValue
            }
          })
          return next
        })
        const answersRedacted = formatAnswersRedacted(savableQuestions, currentIntakeAnswers)
        logger.info(`Intake answers updated for interaction ${interactionId}`, answersRedacted)
      } catch (err) {
        const encounterMovedOn = (err?.response?.status ?? err?.status) === 400
        if (encounterMovedOn) {
          logger.warn(`Intake save rejected for interaction ${interactionId} -- refreshing encounter`, err)
          if (encounterMovedOnNotified.current !== currentEncounterId) {
            encounterMovedOnNotified.current = currentEncounterId
            showInfoToast("This call has moved on to the next patient. Refreshing the form.")
          }
          refetchEncounter?.()
        } else if (!(err instanceof HttpErrors.ThrottledNetworkError)) {
          showErrorToast("Failed to save answers, please try again.")
        }
        logger.error(`Failed to save intake answers for interaction ${interactionId}`, err)
      }
    },
    [
      interactionId,
      intakeAnswers,
      intakeAnswerMode,
      currentEncounterId,
      hasOpenEncounter,
      refetchEncounter,
      showErrorToast,
      showInfoToast,
    ]
  )

  const onDebouncedSetIntakeAnswer = useMemo(
    () => onDebouncedIntakeAnswerChange(setIntakeAnswers),
    [onDebouncedIntakeAnswerChange]
  )

  useEffect(() => {
    if (!intakeLoading) {
      setPendingIntakeRefetch(false)
    }
  }, [intakeLoading, intakeQuestions, intakeError])

  useEffect(() => {
    if (!interactionId || !intakeAnswersChangeDetected || !intakeQuestions || intakeQuestions.length === 0) {
      return
    }
    if (isFetchingEncounter) {
      return
    }
    const doSubmit = exitConfirmation ? true : !intakeAnswersBouncing
    if (!doSubmit) {
      return
    }
    setIntakeAnswersChangeDetected(false)
    saveIntakeAnswers(intakeQuestions)
  }, [
    interactionId,
    intakeAnswersBouncing,
    intakeAnswersChangeDetected,
    exitConfirmation,
    intakeQuestions,
    saveIntakeAnswers,
    isFetchingEncounter,
    currentEncounterId,
  ])

  const onIntakeQuestionChange = useCallback(
    (questionId, value) => {
      if (listeningIn) return
      onDebouncedSetIntakeAnswer((prev) => ({ ...prev, [questionId]: value }))
    },
    [onDebouncedSetIntakeAnswer, listeningIn]
  )

  const createInteractionAnswers = useCallback(() => {
    return createAnswersFromFieldNames(intakeQuestions, {
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      providerFirstName,
      providerLastName,
      phoneNumberAC,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
    })
  }, [
    intakeQuestions,
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    providerFirstName,
    providerLastName,
    phoneNumberAC,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
  ])

  // Assigned during render rather than in an effect. Effects run after render, which leaves a
  // window where a save or load issued for the new encounter compares against the old id and is
  // wrongly abandoned. Async handlers only ever read this ref, never render from it
  liveEncounterIdRef.current = currentEncounterId

  // A refresh request is answered only by a load issued at or after its token, so an earlier
  // in-flight load (e.g. a background poll) cannot settle it.
  const settleAnswersWaiters = useCallback((issuedForToken, ok) => {
    const waiters = answersWaitersRef.current
    answersWaitersRef.current = waiters.filter((waiter) => waiter.token > issuedForToken)
    waiters.filter((waiter) => waiter.token <= issuedForToken).forEach((waiter) => waiter.resolve(ok))
  }, [])

  useEffect(() => {
    if (!interactionId || !intakeQuestions || intakeQuestions.length === 0) {
      setIntakeAnswers({})
      lastLoadedAnswersRef.current = {}
      answersForEncounterIdRef.current = currentEncounterId
      setAnswersForEncounterId(currentEncounterId)
      settleAnswersWaiters(answersRefreshToken, true)
      return
    }

    if (isFetchingEncounter) {
      return
    }

    const loadIntakeAnswers = async () => {
      const issuedForEncounterId = currentEncounterId
      const issuedForToken = answersRefreshToken
      try {
        let tableAnswers = {}
        let interactionAnswers = {}

        if (intakeAnswerMode === "new" || intakeAnswerMode === "both") {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Network timeout: getIntakeAnswers took too long")), 15000)
          )
          const response = await Promise.race([
            dataService.IntakeQuestions.getAnswers({
              interactionId,
              encounterId: issuedForEncounterId,
              intakeQuestions,
            }),
            timeoutPromise,
          ])
          tableAnswers = response?.data || {}
        }

        if (intakeAnswerMode === "old" || intakeAnswerMode === "both") {
          interactionAnswers = createInteractionAnswers()
        }

        if (liveEncounterIdRef.current !== issuedForEncounterId) {
          logger.debug(
            `Discarding intake answers issued for encounter ${issuedForEncounterId}; the call has moved to ${liveEncounterIdRef.current}`
          )
          return
        }

        const mergedAnswers = mergeIntakeAnswers({
          intakeQuestions,
          tableAnswers,
          interactionAnswers,
          mode: intakeAnswerMode,
          isPastFirstEncounter,
          parseAnswerValue,
        })

        const isSameEncounter = answersForEncounterIdRef.current === currentEncounterId
        const lastLoaded = lastLoadedAnswersRef.current
        setAnswersChangedRecently(isSameEncounter && !isEqual(mergedAnswers, lastLoaded))
        setIntakeAnswers((current) => {
          if (!isSameEncounter) return mergedAnswers
          return _mergeWithLocalEdits(current, mergedAnswers, lastLoaded)
        })
        lastLoadedAnswersRef.current = mergedAnswers
        answersForEncounterIdRef.current = currentEncounterId
        setAnswersForEncounterId(currentEncounterId)
        settleAnswersWaiters(issuedForToken, true)
      } catch (err) {
        logger.error(`Failed to load intake answers for interaction ${interactionId}`, err)
        settleAnswersWaiters(issuedForToken, false)
      }
    }

    loadIntakeAnswers()
  }, [
    interactionId,
    intakeAnswerMode,
    createInteractionAnswers,
    intakeQuestions,
    currentEncounterId,
    isPastFirstEncounter,
    isFetchingEncounter,
    answersRefreshToken,
    settleAnswersWaiters,
  ])

  const bumpAnswersToken = useCallback(() => {
    answersTokenRef.current += 1
    setAnswersRefreshToken(answersTokenRef.current)
    return answersTokenRef.current
  }, [])

  // Resolves true once a load issued after this call has applied, false if that load fails.
  const refreshIntakeAnswers = useCallback(
    () => new Promise((resolve) => answersWaitersRef.current.push({ token: bumpAnswersToken(), resolve })),
    [bumpAnswersToken]
  )

  useEffect(() => {
    const intervalMs = Number(answersRefreshIntervalSeconds) * 1000
    if (!interactionId || !multiPatientEnabled || !Number.isFinite(intervalMs) || intervalMs <= 0) {
      return undefined
    }
    const timer = setInterval(bumpAnswersToken, intervalMs)
    return () => clearInterval(timer)
  }, [interactionId, multiPatientEnabled, answersRefreshIntervalSeconds, bumpAnswersToken])

  useEffect(() => {
    const intervalMs = Number(answersRefreshIntervalSeconds) * 1000
    if (!listeningIn || !interactionId || !_isPollInterval(intervalMs)) return undefined
    const timer = setInterval(refreshCallSheet, intervalMs)
    return () => clearInterval(timer)
  }, [listeningIn, interactionId, answersRefreshIntervalSeconds, refreshCallSheet])

  useEffect(() => {
    if (!interactionId || !changeDetected) {
      return
    }
    callFormDataManager.detectChange()
    const doSubmit = exitConfirmation ? true : !bouncing
    if (!doSubmit) {
      return
    }
    setChangeDetected(false)
    callFormDataManager.submit()
  }, [interactionId, bouncing, changeDetected, exitConfirmation])

  const triggerSubmit = usePureCallback(submitCallFormData)
  const triggerRefresh = usePureCallback(refreshCallSheet)

  useEffect(() => {
    callFormDataManager.on("submit", triggerSubmit)
    callFormDataManager.on("refresh", triggerRefresh)
    callFormDataManager.on("running", setRefreshing)

    return () => {
      callFormDataManager.off("submit", triggerSubmit)
      callFormDataManager.off("refresh", triggerRefresh)
      callFormDataManager.off("running", setRefreshing)
    }
  }, [triggerSubmit, triggerRefresh])

  const _refreshCallSheet = useCallback(() => {
    callFormDataManager.refresh()
  }, [])

  useEffect(() => {
    if (!interactionId) {
      return
    }
    return () => callFormDataManager.wrapUp()
  }, [interactionId])

  const showSpinner = useMemo(() => _showSpinner(bouncing, refreshing, saved), [saved, refreshing, bouncing])

  const partnerLocationId = useMemo(() => {
    if (!showCollectLocation) {
      return partnerId
    }
    if (originalPartnerId && partnerId === originalPartnerId) {
      return ""
    }
    return partnerId || ""
  }, [partnerId, originalPartnerId, showCollectLocation])

  const contextValue = useMemo(
    () => ({
      ...accessCodeValidationData,
      ...customFieldsData,
      interactionId: interactionName,
      interactionRecordId: interactionId,
      language,
      unitId,
      partnerLocations: partners,
      partnerLocationId,
      partnerName: originalPartnerName,
      department,
      departmentName,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      providerFirstName,
      providerLastName,
      departmentOptions,
      nonBillableReasons,

      onPatientDOB,
      onNonBillableReasons,
      onPartnerLocation: onPartner,
      onDepartment,
      onPatientFirstName,
      onPatientLastName,
      onPatientMRN,
      onProviderFirstName,
      onProviderLastName,
      refreshCallSheet: _refreshCallSheet,
      fetchCallSheet: refreshCallSheet,
      callSheetVersion,
      refreshIntakeAnswers,
      saved: !showSpinner,
      wizardStatus,
      setWizardStatus,
      phoneNumberAC,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
      onVisitId,
      onSentaraAccessCode,
      onNYUAccessCode,
      onMemberIdNumber,
      onLocation,
      onPhoneNumberAccessCode,
      onBCHOaklandHospital,
      onCallerFirstName,
      onCallerLastName,

      customerPhoneNumber,
      ivrAccessCode,
      meetingUrl,
      showCollectLocation,
      showCollectDepartment,
      partnersLoading,
      intakeQuestions,
      intakeRefetch,
      intakeError,
      intakeAnswers,
      isFetchingIntakeQuestions: pendingIntakeRefetch || intakeLoading,
      onIntakeQuestionChange,
      startNextPatient,
      canStartNextPatient,
      currentEncounterId,
      currentEncounterSequenceNumber,
      isPastFirstEncounter,
      isFetchingEncounter,
      isEncounterFinalized,
      answersForEncounterId,
      answersChangedRecently,
    }),
    [
      accessCodeValidationData,
      customFieldsData,
      interactionName,
      interactionId,
      language,
      unitId,
      partners,
      partnerLocationId,
      originalPartnerName,
      department,
      departmentName,
      patientFirstName,
      patientLastName,
      patientMRN,
      patientDOB,
      providerFirstName,
      providerLastName,
      departmentOptions,
      nonBillableReasons,
      onPatientDOB,
      onNonBillableReasons,
      onPartner,
      onDepartment,
      onPatientFirstName,
      onPatientLastName,
      onPatientMRN,
      onProviderFirstName,
      onProviderLastName,
      _refreshCallSheet,
      refreshCallSheet,
      callSheetVersion,
      refreshIntakeAnswers,
      showSpinner,
      wizardStatus,
      setWizardStatus,
      phoneNumberAC,
      bchOaklandHospitalConfirmation,
      location,
      memberIdNumber,
      nyuAccessCode,
      sentaraAccessCode,
      visitId,
      callerFirstName,
      callerLastName,
      onVisitId,
      onSentaraAccessCode,
      onNYUAccessCode,
      onMemberIdNumber,
      onLocation,
      onPhoneNumberAccessCode,
      onBCHOaklandHospital,
      onCallerFirstName,
      onCallerLastName,
      customerPhoneNumber,
      ivrAccessCode,
      meetingUrl,
      showCollectLocation,
      showCollectDepartment,
      partnersLoading,
      intakeQuestions,
      intakeRefetch,
      intakeError,
      intakeAnswers,
      pendingIntakeRefetch,
      intakeLoading,
      onIntakeQuestionChange,
      startNextPatient,
      canStartNextPatient,
      currentEncounterId,
      currentEncounterSequenceNumber,
      isPastFirstEncounter,
      isFetchingEncounter,
      isEncounterFinalized,
      answersForEncounterId,
      answersChangedRecently,
    ]
  )

  return <CallFormDataContext.Provider value={contextValue}>{children}</CallFormDataContext.Provider>
}

export const CallFormDataProvider = memo(_CallFormDataProvider)

export const useCallFormData = createContextHook(
  CallFormDataContext,
  "useCallFormData must be used within a CallFormDataContext"
)

export default CallFormDataContext
